<script>
  import { onDestroy, tick } from "svelte";
  import { fade, fly } from "svelte/transition";

  export let open = false;
  export let heading = "Pax AI";
  export let subheading = "Assistant";
  export let contextTitle = "Context";
  export let contextItems = [];
  export let contextText = "";
  export let requestAnswer = null;
  export let starterQuestions = [];
  export let showLauncher = false;
  export let bottomOffset = "6.5rem";
  export let mascotBottomOffset = "0rem";
  export let showMascot = true;

  let paxbotInput = "";
  let paxbotInputElement;
  let paxbotLanguage = "en";
  let paxbotLoading = false;
  let paxbotMessagesElement;
  let paxbotScrollSignature = "";
  let paxbotTyping = false;
  let paxbotTypingTimer;
  let previousPaxbotLanguage = paxbotLanguage;
  const paxbotLanguages = [
    { key: "en", label: "EN", name: "English" },
    { key: "de", label: "DE", name: "Deutsch" },
    { key: "es", label: "ES", name: "Español" },
    { key: "fr", label: "FR", name: "Français" },
    { key: "it", label: "IT", name: "Italiano" },
  ];
  const paxbotLanguageText = {
    en: {
      hello:
        "Hi, I'm Pax AI. Ask me anything about the current view. I can also help with navigation or interpretation questions.",
      starterQuestions: [
        "What should I look at first?",
        "Explain this view",
        "Where are the biggest changes?",
        "What is important here?",
      ],
      disclaimer: "Pax AI can make mistakes. Check important results.",
      thinking: "Pax AI is thinking...",
      replying: "Pax AI is replying...",
      placeholder: "Ask Pax AI...",
    },
    de: {
      hello:
        "Hi, ich bin Pax AI. Frag mich gerne etwas zur aktuellen Ansicht. Ich helfe dir auch bei Navigations- oder Verständnisfragen.",
      starterQuestions: [
        "Was soll ich zuerst prüfen?",
        "Erkläre diese Ansicht",
        "Wo sind die größten Änderungen?",
        "Was ist hier wichtig?",
      ],
      disclaimer: "Pax AI kann Fehler machen. Prüfe wichtige Ergebnisse.",
      thinking: "Pax AI denkt nach...",
      replying: "Pax AI antwortet...",
      placeholder: "Pax AI fragen...",
    },
    es: {
      hello:
        "Hola, soy Pax AI. Pregúntame sobre la vista actual. También puedo ayudarte con navegación o interpretación.",
      starterQuestions: [
        "¿Qué debería revisar primero?",
        "Explica esta vista",
        "¿Dónde están los mayores cambios?",
        "¿Qué es importante aquí?",
      ],
      disclaimer: "Pax AI puede cometer errores. Revisa los resultados importantes.",
      thinking: "Pax AI está pensando...",
      replying: "Pax AI está respondiendo...",
      placeholder: "Pregunta a Pax AI...",
    },
    fr: {
      hello:
        "Bonjour, je suis Pax AI. Posez-moi vos questions sur la vue actuelle. Je peux également vous aider à naviguer ou à interpréter les données.",
      starterQuestions: [
        "Que dois-je examiner en premier ?",
        "Explique cette vue",
        "Où sont les plus grands changements ?",
        "Qu'est-ce qui est important ici ?",
      ],
      disclaimer: "Pax AI peut faire des erreurs. Vérifiez les résultats importants.",
      thinking: "Pax AI réfléchit...",
      replying: "Pax AI répond...",
      placeholder: "Demander à Pax AI...",
    },
    it: {
      hello:
        "Ciao, sono Pax AI. Chiedimi qualcosa sulla vista attuale. Posso aiutarti anche con navigazione o interpretazione.",
      starterQuestions: [
        "Cosa dovrei controllare prima?",
        "Spiega questa vista",
        "Dove sono i maggiori cambiamenti?",
        "Cosa è importante qui?",
      ],
      disclaimer: "Pax AI può commettere errori. Verifica i risultati importanti.",
      thinking: "Pax AI sta pensando...",
      replying: "Pax AI sta rispondendo...",
      placeholder: "Chiedi a Pax AI...",
    },
  };
  $: paxbotLocale = paxbotLanguageText[paxbotLanguage] || paxbotLanguageText.en;
  $: visibleStarterQuestions = Array.isArray(starterQuestions) && starterQuestions.length
    ? starterQuestions
    : paxbotLocale.starterQuestions;
  let paxbotChatMessages = [{ role: "assistant", text: paxbotLanguageText.en.hello }];
  $: normalizedContextItems = Array.isArray(contextItems) ? contextItems : [];
  $: normalizedContextText =
    typeof contextText === "string" && contextText.trim() ? contextText : "";

  async function scrollPaxbotToBottom() {
    await tick();
    if (!paxbotMessagesElement) return;
    paxbotMessagesElement.scrollTop = paxbotMessagesElement.scrollHeight;
  }

  function clearPaxbotTyping() {
    if (paxbotTypingTimer) {
      clearTimeout(paxbotTypingTimer);
      paxbotTypingTimer = null;
    }
    paxbotTyping = false;
  }

  function animatePaxbotAnswer(answer) {
    clearPaxbotTyping();
    const fullText = String(answer || "").trim();
    const typingId = `paxbot-${Date.now()}-${Math.random()}`;
    let visibleLength = 0;
    paxbotTyping = true;
    paxbotChatMessages = [
      ...paxbotChatMessages,
      { role: "assistant", text: "", typingId },
    ];

    const typeNext = () => {
      visibleLength = Math.min(
        visibleLength + (fullText.length > 180 ? 3 : 2),
        fullText.length,
      );
      paxbotChatMessages = paxbotChatMessages.map((message) =>
        message.typingId === typingId
          ? { ...message, text: fullText.slice(0, visibleLength) }
          : message,
      );
      if (visibleLength < fullText.length) {
        paxbotTypingTimer = setTimeout(typeNext, 14);
      } else {
        paxbotTypingTimer = null;
        paxbotTyping = false;
      }
    };

    typeNext();
  }

  $: {
    const lastPaxbotMessage = paxbotChatMessages[paxbotChatMessages.length - 1];
    const nextPaxbotScrollSignature = [
      open,
      paxbotLoading,
      paxbotChatMessages.length,
      lastPaxbotMessage?.role || "",
      lastPaxbotMessage?.text || "",
    ].join("|");
    if (open && nextPaxbotScrollSignature !== paxbotScrollSignature) {
      paxbotScrollSignature = nextPaxbotScrollSignature;
      scrollPaxbotToBottom();
    }
  }

  async function handlePaxbotQuestion(questionText = paxbotInput) {
    const question = String(questionText || "").trim();
    if (!question || paxbotLoading || paxbotTyping) return;

    const history = paxbotChatMessages;
    paxbotChatMessages = [
      ...paxbotChatMessages,
      { role: "user", text: question },
    ];
    paxbotInput = "";
    resizePaxbotInput();
    paxbotLoading = true;

    try {
      const language = paxbotLanguages.find(
        (item) => item.key === paxbotLanguage,
      )?.name || "English";
      const answer = requestAnswer
        ? await requestAnswer(question, history, language)
        : paxbotLocale.hello;
      paxbotLoading = false;
      animatePaxbotAnswer(answer);
    } catch (error) {
      clearPaxbotTyping();
      paxbotChatMessages = [
        ...paxbotChatMessages,
        {
          role: "assistant",
          text: `Pax AI could not retrieve an answer right now: ${error.message}`,
        },
      ];
    } finally {
      paxbotLoading = false;
    }
  }

  function handleStarterQuestion(question) {
    paxbotInput = question;
    handlePaxbotQuestion(question);
  }

  function handlePaxbotLanguageChange() {
    if (
      paxbotChatMessages.length === 1 &&
      paxbotChatMessages[0]?.role === "assistant"
    ) {
      paxbotChatMessages = [{ role: "assistant", text: paxbotLocale.hello }];
    }
  }

  $: if (paxbotLanguage !== previousPaxbotLanguage) {
    previousPaxbotLanguage = paxbotLanguage;
    handlePaxbotLanguageChange();
  }

  function resetPaxbotChat() {
    clearPaxbotTyping();
    paxbotLoading = false;
    paxbotInput = "";
    resizePaxbotInput();
    paxbotChatMessages = [{ role: "assistant", text: paxbotLocale.hello }];
  }

  async function resizePaxbotInput() {
    await tick();
    if (!paxbotInputElement) return;
    paxbotInputElement.style.height = "auto";
    const maxHeight = parseFloat(getComputedStyle(paxbotInputElement).maxHeight);
    const nextHeight = Math.min(paxbotInputElement.scrollHeight, maxHeight);
    paxbotInputElement.style.height = `${nextHeight}px`;
    paxbotInputElement.style.overflowY =
      paxbotInputElement.scrollHeight > nextHeight ? "auto" : "hidden";
  }

  function handlePaxbotKeydown(event) {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handlePaxbotQuestion();
    }
  }

  function closePaxbot() {
    open = false;
  }

  onDestroy(() => {
    clearPaxbotTyping();
  });
</script>

{#if open}
  <section
    class="paxbot_chat_window"
    aria-label="Pax AI chat"
    style:bottom={bottomOffset}
    in:fly={{ y: 12, duration: 180 }}
    out:fade={{ duration: 120 }}
  >
    <header class="paxbot_chat_header">
      <div>
        <strong>{heading}</strong>
        <span>{subheading}</span>
      </div>
      <div class="paxbot_header_actions">
        <select
          class="paxbot_language_select"
          bind:value={paxbotLanguage}
          aria-label="Pax AI language"
        >
          {#each paxbotLanguages as language}
            <option value={language.key}>{language.label}</option>
          {/each}
        </select>
        <button
          type="button"
          class="paxbot_reset_button"
          aria-label="Start new chat"
          title="Start new chat"
          on:click={resetPaxbotChat}
        >+</button>
        <button
          type="button"
          class="paxbot_close_button"
          aria-label="Close Pax AI"
          title="Close Pax AI"
          on:click={closePaxbot}
        >
          <img src="/img/assistant/icons/close.svg" alt="" />
        </button>
      </div>
    </header>
    <div class="paxbot_chat_messages" bind:this={paxbotMessagesElement}>
      {#if normalizedContextItems.length || normalizedContextText}
        <div class="paxbot_context_message">
          <strong>{contextTitle}</strong>
          {#if normalizedContextItems.length}
            {#each normalizedContextItems as item}
              <div class="paxbot_context_item">
                {#if item.title}
                  <span>{item.title}</span>
                {/if}
                <p>{item.description || item.text || item}</p>
              </div>
            {/each}
          {:else}
            <p>{normalizedContextText}</p>
          {/if}
        </div>
      {/if}
      {#each paxbotChatMessages as message}
        <div
          class:paxbot_user_message={message.role === "user"}
          class="paxbot_message_row"
        >
          {#if message.role === "assistant"}
            <img src="/img/assistant/paxbot_chatsymbol.svg" alt="" />
          {/if}
          <div class="paxbot_message_bubble">
            <span>{message.role === "user" ? "You" : "Paxbot"}</span>
            <p>{message.text}</p>
          </div>
        </div>
      {/each}
      {#if paxbotLoading}
        <div class="paxbot_message_row">
          <img src="/img/assistant/paxbot_chatsymbol.svg" alt="" />
          <div class="paxbot_message_bubble">
            <span>Pax AI</span>
            <p>{paxbotLocale.thinking}</p>
          </div>
        </div>
      {/if}
    </div>
    <div class="paxbot_starter_chips">
      {#each visibleStarterQuestions as question}
        <button
          type="button"
          disabled={paxbotLoading || paxbotTyping}
          on:click={() => handleStarterQuestion(question)}
        >{question}</button>
      {/each}
    </div>
    <form
      class="paxbot_chat_input"
      on:submit|preventDefault={() => handlePaxbotQuestion()}
    >
      <textarea
        bind:this={paxbotInputElement}
        bind:value={paxbotInput}
        rows="1"
        placeholder={paxbotLoading
          ? paxbotLocale.thinking
          : paxbotTyping
            ? paxbotLocale.replying
            : paxbotLocale.placeholder}
        aria-label="Ask Pax AI"
        on:input={resizePaxbotInput}
        on:keydown={handlePaxbotKeydown}
      ></textarea>
      <button
        type="submit"
        aria-label="Send message"
        title="Send message"
        disabled={!paxbotInput.trim() || paxbotLoading || paxbotTyping}
      >
        <img src="/img/assistant/icons/arrow_right.svg" alt="" />
      </button>
    </form>
    <div class="paxbot_disclaimer">
      {paxbotLocale.disclaimer}
    </div>
  </section>
  {#if showMascot}
    <img
      class="paxbot_chat_mascot"
      in:fly={{ x: 20 }}
      out:fly={{ x: 20, delay: 120 }}
      src="/img/assistant/paxbot_portrait.png"
      alt="Pax AI"
      style:bottom={mascotBottomOffset}
    />
  {/if}
{:else if showLauncher}
  <button
    type="button"
    class="paxbot_launcher"
    title="Open Pax AI"
    aria-label="Open Pax AI"
    on:click={() => (open = true)}
  >
    <img src="/icons/bot.svg" alt="" />
  </button>
{/if}

<style>
  .paxbot_chat_window {
    position: fixed;
    right: 1rem;
    bottom: 6.5rem;
    z-index: 4;
    width: min(28rem, calc(100% - 2rem));
    height: min(37rem, calc(100% - 8rem));
    min-height: 18rem;
    display: flex;
    flex-direction: column;
    border-radius: 0.5rem;
    background: #fff;
    border: 1px solid #e1e4e8;
    box-shadow: 0 1rem 2.5rem rgba(24, 28, 34, 0.18);
    overflow: visible;
  }

  .paxbot_chat_header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding: 0.9rem 1rem;
    background: var(--pax-orange);
    color: white;
  }

  .paxbot_chat_header div {
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 0.15rem;
  }

  .paxbot_header_actions {
    display: flex !important;
    flex: 0 0 auto;
    flex-direction: row !important;
    align-items: center;
    gap: 0.35rem !important;
  }

  .paxbot_language_select {
    height: 1.75rem;
    flex: 0 0 auto;
    border: 1px solid rgba(255, 255, 255, 0.55);
    border-radius: 0.35rem;
    background: rgba(255, 255, 255, 0.14);
    color: #fff;
    font-size: 0.72rem;
    font-weight: 800;
    padding: 0 0.35rem;
    cursor: pointer;
  }

  .paxbot_language_select option {
    color: var(--pax-black);
  }

  .paxbot_chat_header strong {
    font-size: 0.95rem;
    line-height: 1.2;
  }

  .paxbot_chat_header span {
    font-size: 0.75rem;
    opacity: 0.86;
  }

  .paxbot_reset_button,
  .paxbot_close_button {
    height: 1.75rem;
    width: 1.75rem;
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 0;
    border-radius: 0.35rem;
    background: transparent;
    cursor: pointer;
  }

  .paxbot_reset_button {
    border: 1px solid rgba(255, 255, 255, 0.55);
    background: rgba(255, 255, 255, 0.14);
    color: #fff;
    font-size: 1.05rem;
    font-weight: 700;
    line-height: 1;
  }

  .paxbot_reset_button:hover,
  .paxbot_close_button:hover {
    background: rgba(255, 255, 255, 0.16);
  }

  .paxbot_close_button img {
    height: 0.75rem;
    width: 0.75rem;
    filter: brightness(0) invert(1);
  }

  .paxbot_chat_messages {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    padding: 1rem;
    background: #f7f8fa;
  }

  .paxbot_chat_messages::-webkit-scrollbar {
    width: 0.45rem;
  }

  .paxbot_chat_messages::-webkit-scrollbar-thumb {
    border-radius: 99px;
    background: #c9ced6;
  }

  .paxbot_context_message {
    display: flex;
    flex-direction: column;
    gap: 0.45rem;
    padding: 0.75rem;
    border-radius: 0.45rem;
    border: 1px solid #e0e4e8;
    background: #fff;
    color: #24282d;
    font-size: 0.78rem;
  }

  .paxbot_context_message strong,
  .paxbot_context_item span {
    font-size: 0.78rem;
    line-height: 1.3;
  }

  .paxbot_context_message p,
  .paxbot_context_item p,
  .paxbot_message_bubble p {
    margin: 0;
    line-height: 1.4;
    white-space: pre-line;
    overflow-wrap: anywhere;
  }

  .paxbot_context_item {
    display: flex;
    flex-direction: column;
    gap: 0.2rem;
  }

  .paxbot_message_row {
    display: flex;
    gap: 0.5rem;
    align-items: flex-start;
  }

  .paxbot_message_row img {
    width: 1rem;
    height: 1rem;
    margin-top: 0.35rem;
    flex: 0 0 auto;
  }

  .paxbot_message_row.paxbot_user_message {
    justify-content: flex-end;
  }

  .paxbot_message_bubble {
    max-width: 82%;
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    padding: 0.7rem 0.8rem;
    border-radius: 0.45rem;
    background: #fff;
    color: #24282d;
    border: 1px solid #e4e6ea;
    font-size: 0.82rem;
  }

  .paxbot_user_message .paxbot_message_bubble {
    background: var(--pax-black);
    color: #fff;
    border-color: var(--pax-black);
  }

  .paxbot_message_bubble span {
    font-size: 0.72rem;
    font-weight: 700;
    opacity: 0.75;
  }

  .paxbot_disclaimer {
    padding: 0 0.85rem 0.55rem;
    background: #fff;
    color: #8a929b;
    font-size: 0.68rem;
    line-height: 1.25;
    text-align: center;
    border-bottom-left-radius: 0.5rem;
    border-bottom-right-radius: 0.5rem;
  }

  .paxbot_starter_chips {
    display: flex;
    gap: 0.4rem;
    flex-wrap: wrap;
    max-height: none;
    overflow: hidden;
    padding: 0.5rem 0.75rem 0.85rem;
    background: #fff;
    border-top: 1px solid #eef0f2;
  }

  .paxbot_starter_chips button {
    flex: 1 1 calc(50% - 0.25rem);
    max-width: 100%;
    height: 1.75rem;
    min-width: 0;
    border: 1px solid #d7dbe0;
    border-radius: 99px;
    background: #fff;
    color: #3a3f46;
    font-size: 0.72rem;
    font-weight: 700;
    padding: 0 0.65rem;
    cursor: pointer;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .paxbot_starter_chips button:hover:not(:disabled) {
    border-color: var(--pax-orange);
    color: var(--pax-orange);
  }

  .paxbot_starter_chips button:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }

  .paxbot_chat_input {
    display: grid;
    grid-template-columns: 1fr 2.5rem;
    gap: 0.5rem;
    padding: 0.35rem 0.75rem 0.5rem;
    background: #fff;
  }

  .paxbot_chat_input textarea {
    min-height: 2.5rem;
    max-height: calc((0.82rem * 1.35 * 5) + 1.3rem + 2px);
    resize: none;
    border: 1px solid #d7dbe0;
    border-radius: 0.5rem;
    padding: 0.65rem 0.75rem;
    font: inherit;
    font-size: 0.82rem;
    line-height: 1.35;
    overflow-y: hidden;
    outline: none;
  }

  .paxbot_chat_input textarea:focus {
    border-color: var(--pax-orange);
  }

  .paxbot_chat_input button {
    height: 2.5rem;
    width: 2.5rem;
    border: 0;
    border-radius: 0.5rem;
    background: var(--pax-black);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
  }

  .paxbot_chat_input button:disabled {
    cursor: not-allowed;
    opacity: 0.45;
  }

  .paxbot_chat_input button:not(:disabled):hover {
    background: #2a2a2f;
  }

  .paxbot_chat_input button img {
    width: 1rem;
    height: 1rem;
    filter: brightness(0) invert(1);
  }

  .paxbot_chat_mascot {
    position: fixed;
    right: 1.55rem;
    bottom: 0;
    z-index: 5;
    height: 5.6rem;
    width: auto;
    pointer-events: none;
  }

  .paxbot_launcher {
    position: fixed;
    right: 1rem;
    bottom: 1rem;
    z-index: 4;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 2.75rem;
    height: 2.75rem;
    border: 0;
    border-radius: 0.5rem;
    background: var(--pax-orange);
    cursor: pointer;
    box-shadow: 0 0.5rem 1.25rem rgba(24, 28, 34, 0.18);
  }

  .paxbot_launcher:hover {
    background: #e95816;
  }

  .paxbot_launcher img {
    width: 1.2rem;
    height: 1.2rem;
    filter: brightness(0) invert(1);
  }

  @media (max-width: 720px) {
    .paxbot_chat_window {
      right: 0.5rem;
      width: calc(100% - 1rem);
      height: min(28rem, calc(100% - 7.25rem));
      bottom: 5.5rem;
    }

    .paxbot_chat_mascot {
      right: 1rem;
      bottom: 0;
      height: 4.8rem;
    }
  }
</style>
